import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  purchasesService,
  type AccountsPayableResult,
} from '../../../services/purchases.service'
import type { AccountsPayableExportFilters } from '../../../services/reports.service'
import {
  formatPayableDate,
  formatPayableMoney,
} from '../../purchases/accounts-payable'
import { GlobalFilterBar } from '../../ui/GlobalFilterBar'
import { Button } from '../../ui/button'
import { GlobalPaginator } from '../../ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { TableSkeleton } from '../../ui/table-skeleton'
import { TabsContent } from '../../ui/tabs'

const perPage = 10

const emptyResult: AccountsPayableResult = {
  purchases: {
    current_page: 1,
    data: [],
    from: null,
    to: null,
    per_page: perPage,
    total: 0,
  },
  totals: {
    total_amount: '0.00',
    balance_amount: '0.00',
    paid_amount: '0.00',
    pending_count: 0,
  },
  filters: { sucursales: [], suppliers: [] },
}

const headers = [
  'Folio',
  'Proveedor',
  'Sucursal',
  'Fecha compra',
  'Total',
  'Abonado',
  'Saldo pendiente',
]
const cellClass = 'px-6 py-4 text-table-foreground'
export interface AccountsPayableReportContext {
  filters: AccountsPayableExportFilters
  ready: boolean
}

export function AccountsPayableReport({
  onContextChange,
}: {
  onContextChange?: (context: AccountsPayableReportContext | null) => void
}) {
  const [search, setSearch] = useState('')
  const [branch, setBranch] = useState('all')
  const [supplier, setSupplier] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const hasFilters = Boolean(
    search || branch !== 'all' || supplier !== 'all' || dateFrom || dateTo,
  )
  const filters = useMemo<AccountsPayableExportFilters>(
    () => ({
      search: search || undefined,
      sucursal_id: branch === 'all' ? undefined : Number(branch),
      supplier_id: supplier === 'all' ? undefined : Number(supplier),
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [branch, dateFrom, dateTo, search, supplier],
  )
  const query = useQuery({
    queryKey: ['reports', 'accounts-payable', filters, page],
    queryFn: async () =>
      (
        await purchasesService.getAccountsPayable({
          ...filters,
          page,
          per_page: perPage,
        })
      ).data,
    placeholderData: (previous) => previous,
    retry: false,
  })
  const result = query.data ?? emptyResult
  const isLoading = query.isFetching || query.isPending
  const isError = query.isError
  useEffect(() => {
    onContextChange?.({
      filters,
      ready: query.isSuccess && !isLoading && !isError,
    })
    return () => onContextChange?.(null)
  }, [filters, isLoading, isError, query.isSuccess, onContextChange])
  useEffect(() => {
    if (
      !query.isPlaceholderData &&
      query.data &&
      query.data.purchases.current_page !== page
    ) {
      setPage(query.data.purchases.current_page)
    }
  }, [query.data, query.isPlaceholderData, page])

  const clearFilters = () => {
    setSearch('')
    setBranch('all')
    setSupplier('all')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return (
    <TabsContent value="accounts-payable-report" className="space-y-4">
      <GlobalFilterBar
        show={['search', 'dates']}
        values={{ search, startDate: dateFrom, endDate: dateTo }}
        customOptions={{ searchPlaceholder: 'Folio o proveedor...' }}
        onChange={(changes) => {
          if (changes.search !== undefined) setSearch(changes.search)
          if (changes.startDate !== undefined) setDateFrom(changes.startDate)
          if (changes.endDate !== undefined) setDateTo(changes.endDate)
          setPage(1)
        }}
        onClearFilters={clearFilters}
        hasActiveFilters={hasFilters}
      >
        <Select
          value={branch}
          onValueChange={(value) => {
            setBranch(value)
            setPage(1)
          }}
        >
          <SelectTrigger aria-label="Sucursal">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {result.filters.sucursales.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={supplier}
          onValueChange={(value) => {
            setSupplier(value)
            setPage(1)
          }}
        >
          <SelectTrigger aria-label="Proveedor">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los proveedores</SelectItem>
            {result.filters.suppliers.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </GlobalFilterBar>

      <div className="border-border bg-table-bg text-table-foreground overflow-hidden rounded-lg border shadow-sm">
        {isLoading ? (
          <TableSkeleton
            headers={headers}
            columns={headers.map(() => 'text')}
            rows={10}
          />
        ) : isError ? (
          <div className="flex min-h-52 flex-col items-center justify-center gap-3 p-6 text-center">
            <p className="text-sm">
              No fue posible cargar el reporte de cuentas por pagar.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
            </Button>
          </div>
        ) : result.purchases.data.length === 0 ? (
          <p className="text-card-description px-4 py-12 text-center text-sm">
            No hay cuentas por pagar con los filtros seleccionados.
          </p>
        ) : (
          <>
            <div className="border-border border-b px-6 py-4">
              <h3 className="font-semibold">Resultados</h3>
              <p className="text-muted-foreground text-xs">
                {result.totals.pending_count} compras pendientes
              </p>
            </div>
            <div className="overflow-x-auto">
              <Table className="min-w-[900px]">
                <caption className="sr-only">
                  Reporte de cuentas por pagar
                </caption>
                <TableHeader className="border-border bg-table-header-bg border-b">
                  <TableRow className="hover:bg-transparent">
                    {headers.map((header, index) => (
                      <TableHead
                        key={header}
                        className={`text-table-header-foreground h-auto px-6 py-3 text-xs font-medium tracking-wider uppercase ${index >= 4 ? 'text-right' : ''}`}
                      >
                        {header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_tr]:border-border">
                  {result.purchases.data.map((purchase) => (
                    <TableRow
                      key={purchase.id}
                      className="hover:bg-table-row-hover"
                    >
                      <TableCell className={cellClass}>
                        {purchase.reference_number}
                      </TableCell>
                      <TableCell className={cellClass}>
                        {purchase.supplier.name}
                      </TableCell>
                      <TableCell className={cellClass}>
                        {purchase.sucursal.name}
                      </TableCell>
                      <TableCell className={`${cellClass} whitespace-nowrap`}>
                        {formatPayableDate(purchase.purchase_date)}
                      </TableCell>
                      {[
                        purchase.total_amount,
                        purchase.paid_amount,
                        purchase.balance_amount,
                      ].map((amount, index) => (
                        <TableCell
                          key={index}
                          className={`${cellClass} text-right tabular-nums`}
                        >
                          {formatPayableMoney(amount, purchase.currency)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter className="border-border bg-table-header-bg text-table-foreground border-t font-semibold">
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={4} className={cellClass}>
                      TOTAL GENERAL
                    </TableCell>
                    {[
                      result.totals.total_amount,
                      result.totals.paid_amount,
                      result.totals.balance_amount,
                    ].map((amount, index) => (
                      <TableCell
                        key={index}
                        className={`${cellClass} text-right tabular-nums`}
                      >
                        {formatPayableMoney(amount)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
            <GlobalPaginator
              totalItems={result.purchases.total}
              itemsPerPage={result.purchases.per_page}
              currentPage={result.purchases.current_page}
              onPageChange={setPage}
              from={result.purchases.from ?? undefined}
              to={result.purchases.to ?? undefined}
            />
          </>
        )}
      </div>
    </TabsContent>
  )
}
